import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  Box,
  Flex,
  Text,
  Link,
  Tooltip,
  Icon,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import {
  ExternalLinkIcon,
  CheckCircleIcon,
  EmailIcon,
  ChatIcon,
  PhoneIcon,
  AtSignIcon,
} from "@chakra-ui/icons";
import {useParams} from "react-router-dom";
import SignDocument from "../SignDocument/SignDocument"; // Import the SignDocument component
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cqAPI, Auth, HOST } from "../../lib/Auth";
import {
  faGlobe,
  faPlayCircle
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

const removeTables = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Find all table elements
  const tables = doc.querySelectorAll('table');

  tables.forEach((table) => {
    // Replace the table with its content
    const parent = table.parentNode;
    while (table.firstChild) {
      parent.insertBefore(table.firstChild, table);
    }
    parent.removeChild(table); // Remove the table element itself
  });

  // Serialize the document back to a string
  return doc.body.innerHTML;
};

const sanitizedHtml =(message)=>DOMPurify.sanitize(
  (message.mentions || []).reduce((acc, cur) => {
    return acc.replace(
      RegExp(cur.name, "ig"),
      `<b>${cur.name}</b>`
    );
  }, removeTables(message.body))
);

function Messages({
  messages,
  disableSendButton,
  userInfo,
  colorMode,
  isPatient,
  fetchAllMessages,
  setMessages,
  isMobile
}) {
  const bottomOfList = useRef(null);
  const navigate = useNavigate();
  const { uid } = useParams();
  const [selectedSignatureRequestId, setSelectedSignatureRequestId] =
    useState(null);
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [attachmentBlobUrls, setAttachmentBlobUrls] = useState({});
  const scrollToBottom = () => {
    bottomOfList.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) {
      const timeout = setTimeout(scrollToBottom, 400);
      return () => clearTimeout(timeout);
    }
  }, [attachmentBlobUrls]);

  useEffect(() => {
    const fetchBlobUrls = async () => {
      for (const message of messages) {
        for (const attachment of (message.attachments || [])) {
          if (isImage(attachment.name) && !attachmentBlobUrls[attachment.url]) {
            const blobUrl = await fetchBlobUrl(attachment.url);
            setAttachmentBlobUrls(prevState => ({ ...prevState, [attachment.url]: blobUrl }));
          }
        }
      }
    };

    fetchBlobUrls();
  }, [messages]);

  const fetchBlobUrl = async (attachmentUrl) => {
    try {
      const downloadUrl = await cqAPI.downloadFile(attachmentUrl);
      if (!downloadUrl) {
        console.error("File unavailable for download:", attachmentUrl);
        return null;
      }

      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      return blobUrl;
    } catch (error) {
      console.error("Failed to fetch file:", error);
      return null;
    }
  };

  const cancelPaymentRequest = async (message_id) => {
    const response = await cqAPI.get(
      `/payments/request/cancel?cid=${message_id}`
    );
    if (response.type == "ok") {
      setMessages(
        messages.map((m) => {
          if (m._id === message_id) {
            m.payrequest.cancelled = new Date();
          }
          return m;
        })
      );
    }
  };

  const handleSignModalClose = () => {
    setIsSignModalOpen(false);
    setSelectedSignatureRequestId(null);
  };

  const handleSign = (signatureRequestId) => {
    setSelectedSignatureRequestId(signatureRequestId);
    setIsSignModalOpen(true);
  };

  const handleSignatureSuccess = async () => {
    setIsSignModalOpen(false);
    setSelectedSignatureRequestId(null);
    await fetchAllMessages();
  };

  const cancelSignatureRequest = async (message_id, request_id) => {
    const response = await cqAPI.del(
      `/forms?_uid=${uid}&request_id=${request_id}`
    );
    if (response.type === "ok") {
      setMessages(
        messages.map((m) => {
          if (m._id === message_id) {
            m.formrequest.status = "cancelled";
          }
          return m;
        })
      );
    }
  };

  const downloadFile = async (attachmentUrl, fileName) => {
    try {
      const downloadUrl = await cqAPI.downloadFile(attachmentUrl);
      if (!downloadUrl) {
        console.error("File unavailable for download:", attachmentUrl);
        return null;
      }

      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary link element and trigger the download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName); // Set the file name for the download
      document.body.appendChild(link);
      link.click();

      // Clean up by revoking the blob URL and removing the temporary link
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);

      return blobUrl;
    } catch (error) {
      console.error("Failed to download file:", error);
      return null;
    }
  };

  const displayableMessages = () => messages.filter((m) => m.type !== "draft");

  const getMessageIconAndLabel = (type) => {
    let icons = [];
    switch (type) {
      case "call":
        icons.push({ icon: PhoneIcon, label: "Phone Call" });
        break;
      case "email":
        icons.push({ icon: EmailIcon, label: "Email" });
        break;
      case "sms":
        icons.push({ icon: ChatIcon, label: "SMS" });
        break;
      case "emailsms":
        icons.push({ icon: EmailIcon, label: "Email" });
        icons.push({ icon: ChatIcon, label: "SMS" });
        break;
      case "voicemail":
        icons.push({ icon: AtSignIcon, label: "Voicemail" });
        break;
      case "api":
        icons.push({ as: faGlobe, label: "website chat" });
        break;
      default:
        break;
    }
    return icons;
  };

  const isImage = (fileName) => {
    if (fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'].includes(ext);
    }
    return false;
  };


  return (
    <div className="message-list">
      <div direction="column" height="100%" maxWidth="100%">
            {messages.length === 0 ? (
              <>
                {disableSendButton && (
                  <div
                    className="spinner"
                    style={{ position: "absolute", bottom: "0px" }}
                  ></div>
                )}
              </>
            ) : (
              displayableMessages().map((message, index) => {
                const isUserMessage =
                  message.author === userInfo.user_id ||
                  (message.type === "call" && message.direction === "incoming");
                const seen = !!message?.emailStatus?.status?.opened;
                const bgColor =
                  message.direction === "internal_note"
                    ? "orange.100"
                    : isUserMessage
                    ? "#E2F3F3"
                    : colorMode === "dark"
                    ? "#2C2F40"
                    : "#64728C";

                const messageColor =
                  message.direction === "internal_note"
                    ? "#000"
                    : isUserMessage
                    ? "#092728"
                    : colorMode === "dark"
                    ? "#FFFFFF"
                    : "#F9F9FB";
                const footerColor =
                  message.direction === "internal_note"
                    ? "#000"
                    : isUserMessage
                    ? "#092728"
                    : colorMode === "dark"
                    ? "#FFFFFF"
                    : "#F9F9FB";
                const messageBorderRadius =
                  !isUserMessage ? "16px 0px 16px 16px" : "0px 16px 16px 16px";
                let messageMaxWidth = isUserMessage ? "450px" : "350px"
                if (isMobile){
                  messageMaxWidth = isUserMessage ? "320px" : "300px"
                }

                return (
                  <Flex
                    key={index}
                    direction="column"
                    style={{overflowX : 'hidden'}}
                    align={isUserMessage ? "flex-start" : "flex-end"}
                    my={2}
                    color={messageColor}
                  >
                    <Box
                      p={3}
                      bg={bgColor}
                      borderRadius={messageBorderRadius}
                      position="relative"
                      maxWidth={messageMaxWidth}
                      minWidth="200px"
                      className="inbox-message"
                    >
                    <Text
                      as="p"
                      fontSize="md"
                      align="left"
                      paddingBottom="20px"
                      dangerouslySetInnerHTML={{ __html: sanitizedHtml(message) }}
                    />
                      {message.attachments && message.attachments.length > 0 && (
                        <Flex direction="column">
                          {message.attachments.map((attachment) => (
                            <React.Fragment key={attachment.url}>
                              {isImage(attachment.name) ? (
                                <Link
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const blobUrl = attachmentBlobUrls[attachment.url];
                                    if (blobUrl) {
                                      window.open(blobUrl, "_blank");
                                    }
                                  }}
                                  href="#"
                                >
                                  {attachmentBlobUrls[attachment.url] ? (
                                    <img
                                      src={attachmentBlobUrls[attachment.url]}
                                      alt={attachment.name}
                                      style={{ maxWidth: "100%", marginBottom: "10px" }}
                                    />
                                  ) : (
                                    <Text>Loading...</Text>
                                  )}
                                </Link>
                              ) : (
                                <Link
                                  onClick={(e) => {
                                    e.preventDefault(); // Prevent the link from navigating
                                    downloadFile(attachment.url, attachment.name);
                                  }}
                                  href="#" // Set href to "#" to ensure it's recognized as a clickable link
                                  target="_blank"
                                >
                                  {attachment.name}
                                  <ExternalLinkIcon ml="2" />
                                </Link>
                              )}
                            </React.Fragment>
                          ))}
                        </Flex>
                      )}
                      {message.payrequest &&
                        !message.payrequest.cancelled &&
                        !isPatient &&
                        !message.payrequest.paid && (
                          <Box
                            className="payrequest"
                            style={{ maxWidth: "100%" }}
                          >
                            <Box
                              style={{
                                marginBottom: "20px",
                                color: messageColor,
                              }}
                            >
                              Description: {message.payrequest.description}
                            </Box>
                            <Box
                              style={{
                                marginBottom: "20px",
                                color: messageColor,
                              }}
                            >
                              Amount: ${(message.payrequest.amount/100).toFixed(2)}
                            </Box>
                            <Box
                              style={{
                                textAlign: "right",
                                marginBottom: "20px",
                              }}
                            >
                              <Button
                                bg="red.500" color="white" _hover={{ bg: "red.600" }}
                                onClick={() =>
                                  cancelPaymentRequest(message._id)
                                }
                              >
                                Cancel Payment Request
                              </Button>
                            </Box>
                          </Box>
                        )}
                      {message.payrequest &&
                        !message.payrequest.cancelled &&
                        isPatient &&
                        !message.payrequest.paid && (
                          <Box
                            className="payrequest"
                            style={{ maxWidth: "100%" }}
                          >
                            <Box
                              style={{
                                marginBottom: "20px",
                                color: messageColor,
                              }}
                            >
                              Description: {message.payrequest.description}
                            </Box>
                            <Box
                              style={{
                                marginBottom: "20px",
                                color: messageColor,
                              }}
                            >
                              Amount: ${Math.round(message.payrequest.amount/100).toFixed(2)}
                            </Box>
                            <Box
                              style={{
                                textAlign: "right",
                                marginBottom: "20px",
                              }}
                            >
                              <Button>
                                <Link
                                  target="_blank"
                                  href={`${HOST}/payments/request/pay?bid=${message.payrequest.bid}&cid=${message._id}&_uid=${message.uid}`}
                                >
                                  Make the Payment
                                </Link>
                              </Button>
                            </Box>
                          </Box>
                        )}
                      {message.formrequest &&
                        message.formrequest.status === "awaiting_signature" &&
                        isPatient &&
                        !message.formrequest.signed && (
                          <Box
                            className="payrequest"
                            style={{ maxWidth: "100%" }}
                          >
                            <Box
                              style={{
                                marginBottom: "20px",
                                color: messageColor,
                              }}
                            >
                              Description: {message.formrequest.title}
                            </Box>
                            <Box
                              style={{
                                textAlign: "right",
                                marginBottom: "20px",
                              }}
                            >
                              <Button
                                onClick={() =>
                                  handleSign(message.formrequest.request_id)
                                }
                              >
                                Sign
                              </Button>
                            </Box>
                          </Box>
                        )}
                      {message.formrequest &&
                        !isPatient &&
                        message.formrequest.status === "awaiting_signature" && (
                          <Box
                            className="payrequest"
                            style={{ maxWidth: "100%" }}
                          >
                            <Box
                              style={{
                                marginBottom: "20px",
                                color: messageColor,
                              }}
                            >
                              Description: {message.formrequest.title}
                            </Box>
                            <Box
                              style={{
                                textAlign: "right",
                                marginBottom: "20px",
                              }}
                            >
                              <Button
                                bg="red.500" color="white" _hover={{ bg: "red.600" }}
                                onClick={() =>
                                  cancelSignatureRequest(
                                    message._id,
                                    message.formrequest.request_id
                                  )
                                }
                              >
                                Cancel Signature Request
                              </Button>
                            </Box>
                          </Box>
                        )}
                      {message.payrequest && message.payrequest.cancelled && (
                        <Box
                          className="payrequest"
                          style={{ maxWidth: "100%", color: messageColor }}
                        >
                          <Box style={{ marginBottom: "20px" }}>
                            {message.payrequest.description}
                          </Box>
                          <Box style={{ marginBottom: "20px" }}>
                            Amount: ${Math.round(message.payrequest.amount/100).toFixed(2)}
                          </Box>  
                          <Box
                            style={{
                              textAlign: "right",
                              marginBottom: "20px",
                            }}
                          >
                            Cancelled on{" "}
                            {moment(message.payrequest.cancelled).format(
                              "MMM D, YYYY h:mm a"
                            )}
                          </Box>
                        </Box>
                      )}
                      {message.formrequest &&
                        message.formrequest.status === "cancelled" && (
                          <Box
                            className="payrequest"
                            style={{ maxWidth: "100%", color: messageColor }}
                          >
                            <Box style={{ marginBottom: "20px" }}>
                              {message.formrequest.title}
                            </Box>
                            <Box
                              style={{
                                textAlign: "right",
                                marginBottom: "20px",
                              }}
                            >
                              Cancelled on{" "}
                              {moment(message.formrequest.cancelled).format(
                                "MMM D, YYYY h:mm a"
                              )}
                            </Box>
                          </Box>
                        )}
                      {message.formrequest && message.formrequest.signed && (
                        <Box
                          className="payrequest"
                          style={{ maxWidth: "100%", color: messageColor }}
                        >
                          <Box style={{ marginBottom: "20px" }}>
                            {message.formrequest.title}
                          </Box>
                          <Box
                            style={{
                              textAlign: "right",
                              marginBottom: "20px",
                            }}
                          >
                            Signed on{" "}
                            {moment(message.formrequest.signed).format(
                              "MMM D, YYYY h:mm a"
                            )}
                          </Box>
                        </Box>
                      )}
                      {message.payrequest &&
                        !message.payrequest.cancelled &&
                        message.payrequest.paid && (
                          <Box
                            className="payrequest"
                            style={{ maxWidth: "100%", color: messageColor }}
                          >
                            <Box style={{ marginBottom: "20px" }}>
                              {message.payrequest.description}
                            </Box>
                            <Box style={{ marginBottom: "20px" }}>
                              Amount: ${Math.round(message.payrequest.amount/100).toFixed(2)}
                            </Box> 
                            <Box
                              style={{
                                textAlign: "right",
                                marginBottom: "20px",
                              }}
                            >
                              Paid on{" "}
                              {moment(message.payrequest.paid).format(
                                "MMM D, YYYY h:mm a"
                              )}
                            </Box>
                          </Box>
                        )}

                      <Flex justify="space-between" align="center">
                        <Text fontSize="xs" color={footerColor} mt={2}>
                          {(message.author_name || "").replace(
                            /[^a-zA-Z ]/g,
                            ""
                          )}
                        </Text>
                      </Flex>
                      <Flex
                        justify="space-between"
                        align="center"
                        // className={isUserMessage ? "" : "officeMessageFooter"} // not sure if needed so commented for now
                      >
                        <Text fontSize="xs" color={footerColor} align="left">
                          {moment(message.created).format("MMM D, YYYY h:mm a")}
                        </Text>
                        <Flex align="center" >
                          {message.direction !== "internal_note" &&
                            getMessageIconAndLabel(message.type).map(
                              ({ icon, label, as }, iconIndex) => (
                                <Tooltip key={iconIndex} label={label} hasArrow>
                                  {icon ? (
                                    <Icon
                                      as={icon}
                                      color="#092728"
                                      className="message-icon"
                                      boxSize="32px"
                                      mr={2}
                                    />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={as}
                                      className="message-icon"
                                      size="1x"
                                      style={{ marginRight: "8px" }}
                                    />
                                  )}
                                </Tooltip>
                              )
                            )}
                            {message.type === "call" && !isPatient && (
                            <Flex 
                              _hover={{ cursor: "pointer" }} 
                            onClick={async () => {
                              navigate(`/calls/${message.call_id}?bid=${message.bid}`);
                            }}
                            ><FontAwesomeIcon icon={faPlayCircle} className="message-icon" /></Flex>
                            )}
                          {seen && (
                            <Tooltip label="Message was opened" hasArrow>
                              <Icon
                                as={CheckCircleIcon}
                                color={footerColor}
                                w={3}
                                h={3}
                              />
                            </Tooltip>
                          )}
                        </Flex>
                      </Flex>
                    </Box>
                  </Flex>
                );
              })
            )}
            <div ref={bottomOfList} />
          </div>
      <Modal
        isOpen={isSignModalOpen}
        onClose={handleSignModalClose}
        size="full"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <SignDocument
              signatureRequestId={selectedSignatureRequestId}
              onSuccess={handleSignatureSuccess}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}

export default Messages;
